import {noop} from '@benchling/js-utils';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {RESULTS_ANALYTICS_ACTIONS, SCHEMAS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SampleType from 'constants/SampleType';
import * as Context from 'Context';
import {
  ActionMethod,
  ActionOrigin,
  LegacyAssaySchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import DataTableConfigGenerateModal from 'notebook/data-tables/DataTableConfigGenerateModal';
import {isDataTableFeatureEnabled} from 'notebook/data-tables/DataTableUtil';
import {getStore} from 'redux-stores/Store';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import {
  createRequestTemplate,
  fetchRequestTemplateSpec,
  updateRequestTemplateSpec,
} from 'requests/templates/RequestTemplateActions';
import {RequestTemplateSpec} from 'requests/templates/RequestTemplateConstants';
import AssaySchemaPermissionSettings from 'results/AssaySchemaPermissionSettings';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {FieldType, SchemaField, isRequiredLinkSample} from 'schemas/fields/SchemaFieldConstants';
import {ConfigureSchemaSpec} from 'schemas/spec/ConfigureSchemaSpec';
import {SCHEMA_TYPE_NAMES, SCHEMA_TYPES, SchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import ModalAnchor from 'ui-library/ModalAnchor';
import ErrorList from 'ui-library/spec-editor/ErrorList';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import * as modals from 'util/Modals';
import {isServerRequestError} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import * as AssaySchemaActions from '../AssaySchemaActions';
import AssayStore from '../AssayStore';
import {ASSAY_TYPES, AssaySchema} from './AssaySchemaConstants';
import * as AssaySchemaDAO from './AssaySchemaDAO';
import AssaySchemaToolbar from './AssaySchemaToolbar';
import SchemaMetadataSection from './SchemaMetadataSection';
import setWindowTitleForSchema from './setWindowTitleForSchema';

const ASSAY_SCHEMA_TAB = 'ASSAY_SCHEMA_TAB';
const REQUEST_TEMPLATE_SCHEMA_TAB = 'REQUEST_TEMPLATE_SCHEMA_TAB';
const ASSAY_SCHEMA_ACCESS_POLICIES = 'ASSAY_SCHEMA_ACCESS_POLICIES';

function isRequestTaskSchema(
  schema: AssaySchema | RequestTaskSchemaGraphQL
): schema is RequestTaskSchemaGraphQL {
  // eslint-disable-next-line benchlint/no-in-operator
  return '__typename' in schema && schema.__typename === 'RequestTaskSchemaNode';
}

function schemaHasRequestTemplates(
  schema: AssaySchema | RequestTaskSchemaGraphQL | null | undefined
): boolean {
  return Boolean(
    schema &&
    // eslint-disable-next-line benchlint/no-in-operator
    'requestTemplateIds' in schema &&
    schema.requestTemplateIds &&
    schema.requestTemplateIds.length > 0
  );
}

function shouldRenderPermissionSettingsTab(schema?: AssaySchema | RequestTaskSchemaGraphQL | null): boolean {
  return (
    BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS &&
    BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_REQUEST_PERMISSIONS &&
    schema != null
  );
}

function getAllFields(schema?: AssaySchema | RequestTaskSchemaGraphQL | null): Array<SchemaField> {
  if (!schema) {
    return [];
  }
  return isRequestTaskSchema(schema) ? schema.allFieldsJson : schema.allFields;
}

export interface SchemaEditSectionProps {
  schemaId: string;
  isConfigureDataTableEnabled?: boolean;
  isCreateRequestTemplateEnabled?: boolean;
  isEditableWarehouseTableName: boolean;
  schemaType: keyof typeof SCHEMA_TYPES;
}

export interface EnhancedProps {
  allowEditMetadata: boolean;
  errors: Array<string> | null;
  errorMessage: string | null;
  name?: string;
  ownerName?: string | null;
  schema?: AssaySchema | RequestTaskSchemaGraphQL | null;
  specString: string | null;
  warehouseTableName?: string | null;
  sessionId: string;
  fetchSchema: (schemaId: string, isSpecJsonFormat: boolean) => void;
  updateSchema: (args: {
    schemaId: string;
    schemaName: string | undefined;
    sqlIdentifier?: string | null;
    spec: object;
    isSpecJsonFormat?: boolean;
  }) => void;
}

interface SchemaEditSectionState {
  isDataTableModalOpen: boolean;
}

export class SchemaEditSection extends React.Component<
  SchemaEditSectionProps & EnhancedProps,
  SchemaEditSectionState
> {
  override state: SchemaEditSectionState = {
    isDataTableModalOpen: false,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static defaultProps: any = {
    isConfigureDataTableEnabled: false,
    isCreateRequestTemplateEnabled: false,
  };

  override componentDidMount(): void {
    getStore().dispatch(SchemaConfigReduxActions.resetSchemaSpecUi());
    this.props.fetchSchema(this.props.schemaId, true);
  }

  override componentDidUpdate(prevProps: SchemaEditSectionProps & EnhancedProps): void {
    if (prevProps.schemaId !== this.props.schemaId) {
      getStore().dispatch(SchemaConfigReduxActions.resetSchemaSpecUi());
      this.props.fetchSchema(this.props.schemaId, true);
    }
  }

  _openDataTableModal = (): void => {
    analytics.deprecatedTrackEvent(RESULTS_ANALYTICS_ACTIONS.OPENED_CONFIGURE_RESULT_TABLE_MODAL);
    this.setState({isDataTableModalOpen: true});
  };

  _handleCloseDataTableModal = (): void => {
    this.setState({isDataTableModalOpen: false});
  };

  private newIdProvided = (oldIds: Array<string>, newIds: Array<string>): boolean => {
    for (const newId of newIds) {
      if (!oldIds.includes(newId) || newIds.filter((f) => f === newId).length > 1) {
        return true;
      }
    }
    return false;
  };

  private specHasNewFieldIds = (spec: {fields?: unknown; samplesSchema?: unknown}): boolean => {
    // TODO(BNCH-23182): Define types for spec or have this only fire for specific specs
    if (!Array.isArray(spec?.fields)) {
      return false;
    }
    const fieldIds = spec.fields.map(({id}) => id).filter((id) => id != null);
    const originalFieldIds = this.props.schema ? getAllFields(this.props.schema).map((f) => f.apiId) : [];
    if (this.newIdProvided(originalFieldIds, fieldIds)) {
      return true;
    }

    if (Array.isArray(spec.samplesSchema)) {
      const sampleFieldIds = spec.samplesSchema.map((f) => f.id).filter((id) => id != null);
      const originalSampleFieldIds =
        this.props.schema && !isRequestTaskSchema(this.props.schema) && this.props.schema.sampleGroupSchema
          ? this.props.schema.sampleGroupSchema.allFields.map((f) => f.apiId)
          : [];
      if (this.newIdProvided(originalSampleFieldIds, sampleFieldIds)) {
        return true;
      }
    }

    return false;
  };

  private getValidJsonFields = (fields: unknown): Array<{type: string; id?: string}> => {
    if (!Array.isArray(fields)) {
      return [];
    }
    return fields
      .filter((field) => field && typeof field === 'object' && !Array.isArray(field))
      .filter((field) => field.hasOwnProperty('type') && typeof field.type === 'string')
      .map((field) => ({type: field.type, id: (field as {id?: string}).id}));
  };

  private fieldsIncludeNewBatchLink = (
    updatedFields: Array<{type: string; id?: string}>,
    originalBatchLinkFields: Array<SchemaField>
  ): boolean => {
    const possibleBatchLinkFields = updatedFields.filter((field) => field.type === 'batch_link');
    if (possibleBatchLinkFields.length === 0) {
      return false;
    }

    // Any fields without an ID should be treated as new batch_link fields.
    if (possibleBatchLinkFields.some((field) => !field.id)) {
      return true;
    }

    // Duplicate IDs should also be treated as new batch_link fields.
    const fieldIds = possibleBatchLinkFields.map((field) => field.id);
    if (fieldIds.length !== new Set(fieldIds).size) {
      return true;
    }

    // Left only with fields with unique IDs at this point.
    for (const field of possibleBatchLinkFields) {
      // Fields that existed before but weren't batch_link fields should be treated as new.
      if (!originalBatchLinkFields.some((existingBatchField) => existingBatchField.apiId === field.id)) {
        return true;
      }
    }
    // Everything left should be a preexisting batch_link field.
    return false;
  };

  private specHasNewBatchLinkFields = (spec: {fields?: unknown; samplesSchema?: unknown}): boolean => {
    // Check the `fields` on this schema's spec
    const originalFields = this.props.schema ? getAllFields(this.props.schema) : [];
    const originalBatchLinkFields = originalFields.filter(
      (field) => field.fieldType === FieldType.BATCH_LINK
    );
    if (this.fieldsIncludeNewBatchLink(this.getValidJsonFields(spec.fields), originalBatchLinkFields)) {
      return true;
    }

    // Also check fields in the `samplesSchema` if provided (expected only for legacy requests)
    const originalSampleFields: Array<SchemaField> =
      this.props.schema && !isRequestTaskSchema(this.props.schema) && this.props.schema.sampleGroupSchema
        ? this.props.schema.sampleGroupSchema.allFields
        : [];
    const originalBatchLinkSampleFields = originalSampleFields.filter(
      (originalField) =>
        originalField.requiredLink &&
        isRequiredLinkSample(originalField.requiredLink) &&
        originalField.requiredLink.sampleType === SampleType.BATCH
    );
    if (
      this.fieldsIncludeNewBatchLink(
        this.getValidJsonFields(spec.samplesSchema),
        originalBatchLinkSampleFields
      )
    ) {
      return true;
    }

    // Otherwise assume no new batch_link fields anywhere in the spec
    return false;
  };

  _handleUpdate = async (): Promise<void> => {
    let spec;
    try {
      spec = JSON.parse(this.props.specString!);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      getStore().dispatch(
        SchemaConfigReduxActions.setSchemaSpecError({
          errors: [],
          errorMessage: 'Spec is not valid JSON',
        })
      );
      return;
    }

    // Check if spec is an object (not an array or primitive)
    if (!spec || typeof spec !== 'object' || Array.isArray(spec)) {
      getStore().dispatch(
        SchemaConfigReduxActions.setSchemaSpecError({
          errors: [],
          errorMessage: 'Spec must be a JSON object',
        })
      );
      return;
    }

    if (this.specHasNewFieldIds(spec)) {
      const confirm = await Promise.resolve(
        modals.confirm(
          'New field ID provided',
          <p>
            You have specified an ID for a new field, but all new field IDs will be ignored because they are
            autogenerated by the system.
            <br />
            <br />
            Select <b>OK</b> to acknowledge that new field IDs will be ignored and proceed with the update, or{' '}
            <b>Go Back</b> to continue editing the JSON.
          </p>,
          ButtonStyle.PRIMARY,
          {
            acceptText: 'OK',
            rejectText: 'Go Back',
            enableRedesign: true,
            promiseCompatible: true,
            medium: true,
          }
        )
      );
      if (!confirm) {
        return;
      }
    }

    // Batches are deprecated, including batch link field types. See http://go/batch-deprecation-2025
    // Check both `fields` (for the schema itself) and `samplesSchema` (if it is specified: possible for
    // legacy requests). Run this after the new field ID check, to catch accidental ID changes on preexisting
    // fields.
    if (this.specHasNewBatchLinkFields(spec)) {
      getStore().dispatch(
        SchemaConfigReduxActions.setSchemaSpecError({
          errors: [],
          errorMessage: 'Schema cannot contain new Batch Link fields',
        })
      );
      return;
    }

    this.props.updateSchema({
      schemaId: this.props.schemaId,
      schemaName: this.props.name,
      sqlIdentifier:
        this.props.isEditableWarehouseTableName && this.props.warehouseTableName != null
          ? this.props.warehouseTableName || ''
          : null,
      spec,
      isSpecJsonFormat: true,
    });
  };

  override render(): JSX.Element {
    // schema is only used in DataTableConfigGenerateModal and would otherwise cause infinite throbber for
    // the template editor, which doesn't need the schema
    if (
      (this.props.isConfigureDataTableEnabled && this.props.schema == null) ||
      this.props.specString == null
    ) {
      return (
        <div className="u-flex-grow">
          <HorizontalThrobber key="throbber" />
        </div>
      );
    }
    const isSchemaArchived = this.props.schema != null && isArchived(this.props.schema);

    return (
      <div className="u-flex-grow">
        {this.props.allowEditMetadata && (
          <SchemaMetadataSection
            isEditableWarehouseTableName={this.props.isEditableWarehouseTableName}
            hideWarehouseTableName={this.props.warehouseTableName == null}
            name={this.props.name}
            sqlIdentifier={this.props.warehouseTableName}
            schema={this.props.schema}
            onNameChange={(name: string) => {
              getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecName(name));
            }}
            onOwnerChange={noop}
            onSqlIdentifierChange={(sqlIdentifier: string) => {
              getStore().dispatch(SchemaConfigReduxActions.setSchemaSpecSqlIdentifier(sqlIdentifier));
            }}
          />
        )}
        <ErrorList errors={this.props.errors} errorMessage={this.props.errorMessage} />
        <div>
          <ConfigureSchemaSpec
            link={{
              value: this.props.specString,
              onChange: (e: {target: {value: string}}) => {
                getStore().dispatch(SchemaConfigReduxActions.schemaSpecChanged(e.target.value));
              },
            }}
            readOnly={isSchemaArchived}
          />
        </div>
        <Button
          className="u-small-spacing-right"
          buttonStyle={ButtonStyle.SECONDARY}
          disabled={isSchemaArchived || !this.props.specString}
          onClick={this._handleUpdate}
          size={ButtonSize.MEDIUM}
          text="Update"
          dataTestAttrs={{component: 'SchemaEditSection', key: ModalButtonKey.SUBMIT}}
        />
        {(this.props.schemaType === SCHEMA_TYPES.ASSAY_RUN ||
          this.props.schemaType === SCHEMA_TYPES.ASSAY_RESULT) && (
          <Tooltip
            disabled={(this.props.schema as AssaySchema)?.derivedParent == null}
            title="This schema can only be edited with JSON because it has a derived parent"
          >
            <Button
              className="u-small-spacing-right"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              disabled={(this.props.schema as AssaySchema)?.derivedParent != null}
              onClick={() => {
                analytics.trackEvent(SCHEMAS_ANALYTICS_ACTIONS.CLICKED_EDIT_SCHEMA_VIA_UI, {
                  schema_type:
                    this.props.schemaType === LegacyAssaySchemaType.ASSAY_RUN
                      ? ModelTypeConstants.RUN_SCHEMA
                      : ModelTypeConstants.RESULT_SCHEMA,
                  schema_id: this.props.schemaId,
                  session_id: this.props.sessionId,
                });
                BenchlingUtil.navigate(
                  null,
                  AssaySchemaDAO.assaySchemaURL(
                    this.props.schemaType as LegacyAssaySchemaType,
                    this.props.schemaId
                  )
                );
              }}
              size={ButtonSize.MEDIUM}
              text="Edit in UI"
              dataTestAttrs={{component: 'SchemaEditSection', element: 'switchUI'}}
            />
          </Tooltip>
        )}
        {this.props.isConfigureDataTableEnabled && (
          <span>
            {this.state.isDataTableModalOpen && (
              <ModalAnchor key="data-table-modal" open={true}>
                <DataTableConfigGenerateModal
                  assaySchema={this.props.schema as AssaySchema}
                  onRequestClose={this._handleCloseDataTableModal}
                />
              </ModalAnchor>
            )}
            <Button
              className="u-small-spacing-right"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              disabled={isSchemaArchived}
              onClick={this._openDataTableModal}
              size={ButtonSize.MEDIUM}
              text={`Configure ${MODEL_NAMES.Result_Table}`}
            />
          </span>
        )}
        {this.props.isCreateRequestTemplateEnabled && !schemaHasRequestTemplates(this.props.schema) && (
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            disabled={isSchemaArchived}
            onClick={() => {
              createRequestTemplate(this.props.schemaId);
            }}
            dataTestAttrs={{component: 'SchemaPage', element: 'createRequestTemplateButton'}}
            size={ButtonSize.MEDIUM}
            text={`Create ${getLegacyRequestObjectName({capwords: true})} Template`}
          />
        )}
      </div>
    );
  }
}

class AssaySchemaEditSectionConnector extends DataConnector<SchemaEditSectionProps, EnhancedProps> {
  override stores = [AssayStore];

  getEditingName(stateName: string | null, schema: AssaySchema | null): string {
    if (stateName != null) {
      return stateName;
    } else if (schema) {
      return schema.name;
    } else {
      return '';
    }
  }

  getEditingWarehouseTableName(stateSqlIdentifier: string | null, schema: AssaySchema | null): string | null {
    if (stateSqlIdentifier != null) {
      return stateSqlIdentifier;
    } else {
      return schema && schema.sqlIdentifier;
    }
  }

  trackLabAutomationConfigUpdates(
    props: SchemaEditSectionProps,
    hasUnsavedInputFileConfigData: boolean,
    hasUnsavedOutputFileConfigData: boolean,
    sessionId: string
  ): void {
    const metadata = {
      action_origin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_JSON,
      action_method: ActionMethod.SETTINGS_EDIT,
      schema_type: props.schemaType,
      schema_id: props.schemaId,
      session_id: sessionId,
    };
    if (hasUnsavedInputFileConfigData) {
      analytics.trackEvent(RESULTS_ANALYTICS_ACTIONS.UPDATED_INPUT_FILE_CONFIG, metadata);
    }
    if (hasUnsavedOutputFileConfigData) {
      analytics.trackEvent(RESULTS_ANALYTICS_ACTIONS.UPDATED_OUTPUT_FILE_CONFIG, metadata);
    }
  }

  enhanceProps(): EnhancedProps {
    const schema = AssayStore.getAssaySchema(this.props.schemaId);
    const fetchSchema = (schemaId: string, isSpecJsonFormat: boolean = true): void => {
      AssaySchemaActions.fetchSchemaSpec(schemaId, isSpecJsonFormat);
    };
    const {
      name,
      specString,
      sqlIdentifier,
      errorState,
      sessionId,
      hasUnsavedInputFileConfigData,
      hasUnsavedOutputFileConfigData,
    } = this.reduxState().ui.schemaConfig;
    const updateSchema = async (args: {
      schemaId: string;
      schemaName: string;
      sqlIdentifier?: string | null;
      spec: SchemaSpec;
      isSpecJsonFormat: boolean;
    }): Promise<void> => {
      try {
        await Context.usingAsync(
          {
            analyticsMetadata: {
              actionOrigin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_JSON,
            },
          },
          null,
          () => {
            withLoadingScreen(AssaySchemaActions.updateAssaySchema({...args, sessionId}), 'Updating...');
          }
        );
      } catch (e) {
        if (!isServerRequestError(e)) {
          throw e;
        }
      }
      this.trackLabAutomationConfigUpdates(
        this.props,
        hasUnsavedInputFileConfigData,
        hasUnsavedOutputFileConfigData,
        sessionId
      );
    };
    const {errors, errorMessage} = errorState != null ? errorState : {errors: null, errorMessage: null};

    return {
      allowEditMetadata: true,
      errors,
      errorMessage,
      name: this.getEditingName(name, schema),
      schema,
      specString,
      fetchSchema,
      updateSchema,
      warehouseTableName: this.getEditingWarehouseTableName(sqlIdentifier, schema),
      ownerName: schema && schema.organization.name,
      sessionId,
    };
  }
}

export const AssaySchemaEditSection = AssaySchemaEditSectionConnector.connect(SchemaEditSection);

class RequestTemplateEditSectionConnector extends DataConnector<SchemaEditSectionProps, EnhancedProps> {
  enhanceProps(): EnhancedProps {
    const {specString, errorState, sessionId} = this.reduxState().ui.schemaConfig;
    const {errors, errorMessage} = errorState != null ? errorState : {errors: null, errorMessage: null};
    return {
      allowEditMetadata: false,
      errors,
      errorMessage,
      specString,
      sessionId,
      fetchSchema: (requestTemplateId: string) => {
        this.dispatch(fetchRequestTemplateSpec(requestTemplateId));
      },
      updateSchema: ({
        schemaId,
        schemaName,
        sqlIdentifier,
        spec,
        isSpecJsonFormat,
      }: {
        schemaId: string;
        schemaName: string;
        sqlIdentifier: string | null;
        spec: RequestTemplateSpec;
        isSpecJsonFormat: boolean;
      }) => {
        this.dispatch(updateRequestTemplateSpec(schemaId, spec));
      },
    };
  }
}

const RequestTemplateEditSection = RequestTemplateEditSectionConnector.connect(SchemaEditSection);

export interface SchemaPageEditSectionProps {
  schemaId: string;
  schema?: AssaySchema | RequestTaskSchemaGraphQL | null;
  isConfigureDataTableEnabled?: boolean;
  isCreateRequestTemplateEnabled?: boolean;
  isEditableWarehouseTableName: boolean;
  schemaType: keyof typeof SCHEMA_TYPES;
}

export interface SchemaPageProps {
  schemaId: string;
  schemaType: keyof typeof SCHEMA_TYPES;
}

interface ConnectedSchemaPageProps {
  // Props from connector
  editSectionClass: React.ComponentClass<SchemaPageEditSectionProps>;
  schema: AssaySchema | RequestTaskSchemaGraphQL | null;
  listingUrl: string;
}

interface SchemaPageState {
  activeTab: string;
}

export class SchemaPage extends React.Component<SchemaPageProps & ConnectedSchemaPageProps, SchemaPageState> {
  override state: SchemaPageState = {
    activeTab: ASSAY_SCHEMA_TAB,
  };

  private handleNavigateToSchemas = (): void => {
    BenchlingUtil.navigate(null, this.props.listingUrl);
  };

  private handleSetActiveTab = (key: string): void => {
    this.setState({activeTab: key});
  };

  override render(): JSX.Element {
    setWindowTitleForSchema(this.props.schema, this.props.schemaType);

    const subtitle = this.props.schema ? (
      <span>
        {this.props.schema.name}
        <ArchiveRecordLabel item={this.props.schema} />
      </span>
    ) : (
      ''
    );
    const schemaTypeName = SCHEMA_TYPE_NAMES[this.props.schemaType];
    const EditSectionClass = this.props.editSectionClass;
    return (
      <SettingsPageContainer
        onClickTitle={this.handleNavigateToSchemas}
        subtitle={subtitle}
        title={`${schemaTypeName} Schemas`}
        toolbar={
          this.props.schema && (
            <AssaySchemaToolbar
              schemaId={this.props.schema.id}
              schemaType={this.props.schemaType}
              isSpecJsonFormat={true}
            />
          )
        }
      >
        <TabContainer
          activeTab={this.state.activeTab}
          className="u-flex-grow"
          onRequestTabChange={this.handleSetActiveTab}
        >
          <Tab tabKey={ASSAY_SCHEMA_TAB} title={`${schemaTypeName} Settings`}>
            <EditSectionClass
              schemaId={this.props.schemaId}
              schema={this.props.schema}
              isConfigureDataTableEnabled={
                isDataTableFeatureEnabled() && this.props.schemaType === SCHEMA_TYPES.ASSAY_RESULT
              }
              isCreateRequestTemplateEnabled={this.props.schemaType === SCHEMA_TYPES.ASSAY_REQUEST}
              isEditableWarehouseTableName={
                BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME &&
                Object.keys(ASSAY_TYPES).includes(this.props.schemaType)
              }
              schemaType={this.props.schemaType}
            />
          </Tab>
          {schemaHasRequestTemplates(this.props.schema) && (
            <Tab tabKey={REQUEST_TEMPLATE_SCHEMA_TAB} title="Template Settings">
              <RequestTemplateEditSection
                schemaId={(this.props.schema as AssaySchema).requestTemplateIds[0]}
                schemaType={this.props.schemaType}
                isEditableWarehouseTableName={false}
              />
            </Tab>
          )}
          {shouldRenderPermissionSettingsTab(this.props.schema) && (
            <Tab tabKey={ASSAY_SCHEMA_ACCESS_POLICIES} title="Access Policies">
              <AssaySchemaPermissionSettings assaySchemaId={this.props.schema ? this.props.schema.id : ''} />
            </Tab>
          )}
        </TabContainer>
      </SettingsPageContainer>
    );
  }
}

export default AssayStore.connect(
  (assayStore: typeof AssayStore, ownProps: SchemaPageProps) =>
    ({
      editSectionClass: AssaySchemaEditSection,
      schema: assayStore.getAssaySchema(ownProps.schemaId),
      listingUrl: AssaySchemaDAO.assaySchemasURL(ownProps.schemaType),
    }) as ConnectedSchemaPageProps
)(SchemaPage);
